// Fixed viewport-relative dialog shell. Every long-form edit modal that
// changes height as the user types or switches sections used to "jump" because
// `max-h-[90vh]` lets the dialog shrink to fit short content. This primitive
// locks the height to `h-[90vh]` (fixed, not max-) so the shell stays still
// regardless of inner content height or the active tab/section.
//
// Two scroll modes:
//   - `scroll-shell` (default) — the entire DialogContent scrolls. Use this
//     for forms with a single vertical column of fields.
//   - `section-scroll` — DialogContent is `overflow-hidden flex flex-col`.
//     The caller is responsible for rendering a `flex-1 overflow-y-auto`
//     scroll region (typical when there's a sticky header / footer / sidebar
//     inside the modal — see QuoteFormModal, InvoiceFormModal).
//
// Width sizing maps to Tailwind's `sm:max-w-{size}` scale; widths still honor
// narrower viewports via `max-w-[95vw]`.

import { type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { DialogContent } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

type DialogContentProps = ComponentPropsWithoutRef<typeof DialogContent>;

export type FixedDialogSize =
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl';

const SIZE_CLASS: Record<FixedDialogSize, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
  '6xl': 'sm:max-w-6xl',
  '7xl': 'sm:max-w-7xl',
};

export interface FixedSizeDialogProps {
  /** Tailwind max-width tier — defaults to `2xl`. */
  size?: FixedDialogSize;
  /**
   * Scroll behaviour. `scroll-shell` (default): dialog itself scrolls.
   * `section-scroll`: dialog is overflow-hidden flex-col; caller renders the
   * `flex-1 overflow-y-auto` section.
   */
  scroll?: 'scroll-shell' | 'section-scroll';
  className?: string;
  children: ReactNode;
  /**
   * Forwarded to the underlying Radix `DialogContent`. Use it to control
   * dismissal — e.g. `preventDefault()` an outside-interaction that originated
   * inside another dialog layered on top of this one, so that inner dialog
   * closing doesn't also dismiss this one (a common cause of confirm-dialog
   * loops). A modal form should only close on Escape, a genuine background
   * click, or an explicit button.
   */
  onInteractOutside?: DialogContentProps['onInteractOutside'];
}

export function FixedSizeDialog({
  size = '2xl',
  scroll = 'scroll-shell',
  className,
  children,
  onInteractOutside,
}: FixedSizeDialogProps) {
  return (
    <DialogContent
      className={cn(
        'w-full max-w-[95vw] h-[90vh]',
        SIZE_CLASS[size],
        scroll === 'scroll-shell' ? 'overflow-y-auto' : 'overflow-hidden flex flex-col',
        className,
      )}
      onInteractOutside={onInteractOutside}
    >
      {children}
    </DialogContent>
  );
}
